<script setup>
/**
 * Campo de data/hora do ADVup — digitável e com seletor próprio.
 *
 * Por que existe: o `input[type=datetime-local]` nativo até aceita teclado,
 * mas por segmento (clica no dia, digita, pula para o mês...). Ninguém
 * descobre sozinho, e o calendário do Chrome tem cara de outro produto —
 * cinza, em inglês nas máquinas em outro idioma, sem "Limpar". A equipe do
 * A&T acabava escolhendo tudo no mouse (Victor, 2026-08-04).
 *
 * Aqui:
 *   - o campo é texto com máscara `dd/mm/aaaa hh:mm` — digita corrido, as
 *     barras e os dois-pontos entram sozinhos;
 *   - o seletor é nosso, com a mesma paleta do resto do sistema: calendário
 *     do mês + colunas de hora e minuto, "Agora" e "Limpar";
 *   - ↑/↓ mexem no pedaço onde está o cursor, como no nativo.
 *
 * O v-model fala o mesmo dialeto do input nativo (`YYYY-MM-DDTHH:mm`, ou
 * `YYYY-MM-DD` com `date-only`), então trocar um pelo outro não mexe em nada
 * de quem usa.
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  /** Só data, sem hora (`YYYY-MM-DD`) */
  dateOnly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // `input-field` era o padrão, e essa classe mora no `<style scoped>` de
  /* ESTE CAMPO NUNCA TEVE TEMA ESCURO.
   Todas as cores vinham de `--color-n-slate-*` e `--color-n-weak`, que NAO
   existem neste projeto (armadilha 1.1b): as onze caiam sempre no valor de
   reserva — cinzas claros — nos DOIS temas. No escuro o calendario abria
   branco no meio da tela preta.
   O azul tambem era avulso: `#1f6feb`, que nao e o `#2563eb` da marca. */
  // `AgendaActivityModal.vue`: em qualquer OUTRO componente ela não tem CSS
  // nenhum. O padrão passa a ser o campo global (`_advup-tela.scss`), que
  // existe em toda tela; quem quiser outra pele continua passando a sua.
  inputClass: {
    type: String,
    default: 'advup-campo reset-base',
  },
  /**
   * Não deixa escolher (nem digitar) dia anterior a hoje. É para agendamento:
   * compromisso e prazo acontecem daqui para a frente — marcar para semana
   * passada é sempre erro de digitação (Victor, 2026-08-04).
   */
  noPast: { type: Boolean, default: false },
  /**
   * De quantos em quantos minutos dá para escolher. `30` deixa só :00 e :30
   * na coluna, e arredonda o que for digitado à mão.
   *
   * Pedido da Dra. Jullya (07/09/2026): "pode deixar os horários sempre
   * redondos em 00 ou 30 min". Vale para o agendamento de atendimento, onde
   * 16:33 não quer dizer nada — mas NÃO para prazo processual, que tem hora
   * exata. Por isso é opção, com `1` (todos os minutos) de padrão: as 27
   * telas que já usam este campo continuam idênticas.
   */
  minuteStep: { type: Number, default: 1 },
  // O nome do campo para o leitor de tela. O rótulo visível mora FORA do
  // componente, num `<span>` que não se liga ao input — sem isto a pessoa
  // ouvia só "campo de texto, dd/mm/aaaa hh:mm".
  rotulo: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const texto = ref('');
const aberto = ref(false);
const raiz = ref(null);
const campo = ref(null);
const listaHoras = ref(null);
const listaMinutos = ref(null);
const painel = ref(null);

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const mascara = computed(() =>
  props.dateOnly ? 'dd/mm/aaaa' : 'dd/mm/aaaa hh:mm'
);

// Início de hoje: o limite é o DIA, não o instante — marcar hoje às 08:00 às
// 17:00 continua valendo, porque o dia ainda é hoje.
function inicioDeHoje() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function anteriorAHoje(data) {
  return data < inicioDeHoje();
}

// ── Conversão ───────────────────────────────────────────────────────────────
function isoParaTexto(iso) {
  if (!iso) return '';
  const [data, hora = ''] = String(iso).split('T');
  const [ano, mes, dia] = data.split('-');
  if (!ano || !mes || !dia) return '';
  const base = `${dia}/${mes}/${ano}`;
  if (props.dateOnly) return base;
  return hora ? `${base} ${hora.slice(0, 5)}` : base;
}

// Devolve null enquanto está incompleto: campo pela metade não pode virar
// data válida sem querer (o "01/01/0002" que todo formulário com máscara
// mal-feita produz).
function textoParaIso(valor) {
  const d = String(valor).replace(/\D/g, '');
  const minimo = props.dateOnly ? 8 : 12;
  if (d.length < minimo) return null;

  const dia = d.slice(0, 2);
  const mes = d.slice(2, 4);
  const ano = d.slice(4, 8);
  if (+mes < 1 || +mes > 12 || +dia < 1 || +dia > 31) return null;
  // 31/02 não existe: o Date normalizaria para 03/03 em silêncio.
  const teste = new Date(+ano, +mes - 1, +dia);
  if (teste.getMonth() !== +mes - 1 || teste.getDate() !== +dia) return null;

  if (props.dateOnly) return `${ano}-${mes}-${dia}`;

  const hh = d.slice(8, 10);
  const mm = d.slice(10, 12);
  if (+hh > 23 || +mm > 59) return null;
  return `${ano}-${mes}-${dia}T${hh}:${mm}`;
}

function aplicarMascara(valor) {
  const d = String(valor)
    .replace(/\D/g, '')
    .slice(0, props.dateOnly ? 8 : 12);
  let saida = d.slice(0, 2);
  if (d.length > 2) saida += `/${d.slice(2, 4)}`;
  if (d.length > 4) saida += `/${d.slice(4, 8)}`;
  if (d.length > 8) saida += ` ${d.slice(8, 10)}`;
  if (d.length > 10) saida += `:${d.slice(10, 12)}`;
  return saida;
}

watch(
  () => props.modelValue,
  novo => {
    // Não sobrescreve o que a pessoa está digitando: só sincroniza quando o
    // valor que vem de fora é outro de verdade.
    if (textoParaIso(texto.value) !== (novo || null)) {
      texto.value = isoParaTexto(novo);
    }
  },
  { immediate: true }
);

function emitir(iso) {
  emit('update:modelValue', iso);
}

// Aviso curto embaixo do campo: recusar em silêncio faz a pessoa achar que o
// sistema comeu o que ela digitou.
const avisoPassado = ref(false);
let avisoTimer = null;

function avisarPassado() {
  avisoPassado.value = true;
  if (avisoTimer) clearTimeout(avisoTimer);
  avisoTimer = setTimeout(() => {
    avisoPassado.value = false;
  }, 4000);
}

function doisDigitos(n) {
  return String(n).padStart(2, '0');
}

function paraIso(d) {
  const base = `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}`;
  if (props.dateOnly) return base;
  return `${base}T${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}

// Arredonda para o degrau mais PRÓXIMO, não para baixo: quem digita 16:29
// quis dizer 16:30, e truncar daria 16:00 — meia hora de diferença num
// compromisso. 16:45 sobe para 17:00 (o degrau seguinte é a hora cheia).
function ajustarAoPasso(d) {
  const passo = Math.max(1, Math.min(60, props.minuteStep));
  if (passo === 1) return d;
  const arredondado = Math.round(d.getMinutes() / passo) * passo;
  const saida = new Date(d);
  saida.setMinutes(0, 0, 0);
  saida.setMinutes(arredondado);
  return saida;
}

// MEDIDO em 08/09/2026: o arredondamento só valia para o CLIQUE, porque a
// digitação emite direto, sem passar pelo `aplicarData`. Digitar 16:37 com
// passo 30 gravava 16:37. Este helper fecha o outro caminho.
function isoAjustado(iso) {
  const passo = Math.max(1, Math.min(60, props.minuteStep));
  if (passo === 1 || props.dateOnly || !iso) return iso;
  return paraIso(ajustarAoPasso(new Date(iso)));
}

function onInput(evento) {
  texto.value = aplicarMascara(evento.target.value);
  const iso = textoParaIso(texto.value);
  if (texto.value === '') {
    emitir('');
    return;
  }
  if (!iso) return;
  // Data no passado não emite: o campo mostra o que foi digitado (senão a
  // pessoa não entende por que sumiu), mas o valor não sobe.
  if (props.noPast && anteriorAHoje(new Date(`${iso.slice(0, 10)}T00:00`))) {
    return;
  }
  // Arredonda o VALOR já na digitação, mas deixa o texto como está enquanto a
  // pessoa escreve — corrigir no meio da digitação faz o cursor pular. O
  // texto é acertado no blur, logo abaixo.
  emitir(isoAjustado(iso));
}

// Data digitada pela metade não vira valor: no blur, ou está completa ou
// volta ao que o modelo tem. Sem isso o campo mente para o usuário.
function onBlur() {
  if (texto.value === '') return;
  const iso = textoParaIso(texto.value);
  const passado =
    props.noPast && iso && anteriorAHoje(new Date(`${iso.slice(0, 10)}T00:00`));

  if (!iso || passado) {
    texto.value = isoParaTexto(props.modelValue);
    // Sincroniza o DOM na mão: se `texto` já era '' antes, o Vue não vê
    // mudança e não repinta o input — o campo ficaria mostrando a data
    // recusada como se tivesse sido aceita.
    if (campo.value) campo.value.value = texto.value;
    if (passado) avisarPassado();
    return;
  }

  // Campo nunca mostra um horário diferente do que foi gravado. Sem isto,
  // quem digita 16:37 com passo 30 lê "16:37" na tela e tem 16:30 no banco.
  const ajustado = isoAjustado(iso);
  if (ajustado !== iso) {
    texto.value = isoParaTexto(ajustado);
    if (campo.value) campo.value.value = texto.value;
    emitir(ajustado);
  }
}

function aplicarData(d, { manterCursor } = {}) {
  // Passa pelo arredondamento AQUI porque este é o funil: clique no dia,
  // clique na hora, ↑/↓ no teclado e digitação no seletor terminam todos
  // neste ponto. Com `minuteStep = 1` (o padrão) é identidade.
  const iso = paraIso(ajustarAoPasso(d));
  texto.value = isoParaTexto(iso);
  emitir(iso);
  if (manterCursor !== undefined) {
    nextTick(() => campo.value?.setSelectionRange(manterCursor, manterCursor));
  }
}

// ── ↑/↓ no pedaço sob o cursor, como no campo nativo ────────────────────────
const SEGMENTOS = [
  { ini: 0, fim: 2, passo: 'dia' },
  { ini: 3, fim: 5, passo: 'mes' },
  { ini: 6, fim: 10, passo: 'ano' },
  { ini: 11, fim: 13, passo: 'hora' },
  { ini: 14, fim: 16, passo: 'minuto' },
];

function ajustar(delta) {
  const iso = textoParaIso(texto.value);
  if (!iso) return;
  const pos = campo.value?.selectionStart ?? 0;
  const seg = SEGMENTOS.find(s => pos >= s.ini && pos <= s.fim) || SEGMENTOS[0];

  const d = new Date(props.dateOnly ? `${iso}T00:00` : iso);
  if (seg.passo === 'dia') d.setDate(d.getDate() + delta);
  if (seg.passo === 'mes') d.setMonth(d.getMonth() + delta);
  if (seg.passo === 'ano') d.setFullYear(d.getFullYear() + delta);
  if (seg.passo === 'hora') d.setHours(d.getHours() + delta);
  if (seg.passo === 'minuto') d.setMinutes(d.getMinutes() + delta);

  aplicarData(d, { manterCursor: pos });
}

function onKeydown(evento) {
  if (evento.key === 'ArrowUp' || evento.key === 'ArrowDown') {
    evento.preventDefault();
    ajustar(evento.key === 'ArrowUp' ? 1 : -1);
  }
  if (evento.key === 'Escape') aberto.value = false;
}

// Estado do teclado dentro do seletor: qual coluna recebe o próximo dígito
// e o que já foi digitado nos últimos instantes ("2" + "2" = 22h).
const colunaAtiva = ref('hora');
const buffer = ref('');
let bufferTimer = null;

function limparBuffer() {
  buffer.value = '';
  if (bufferTimer) clearTimeout(bufferTimer);
  bufferTimer = null;
}

// ── Seletor ─────────────────────────────────────────────────────────────────
const mesVisivel = ref(new Date());

const selecionada = computed(() => {
  const iso = textoParaIso(texto.value) || props.modelValue;
  if (!iso) return null;
  return new Date(props.dateOnly ? `${iso}T00:00` : iso);
});

const horaSelecionada = computed(() => selecionada.value?.getHours() ?? 0);
const minutoSelecionado = computed(() => selecionada.value?.getMinutes() ?? 0);

// "Agosto de 2026" — só a inicial do mês em maiúscula. O `capitalize` do CSS
// escrevia "Agosto De 2026", com o "De" no meio.
const tituloMes = computed(() => {
  const mes = MESES[mesVisivel.value.getMonth()];
  return `${mes[0].toUpperCase()}${mes.slice(1)} de ${mesVisivel.value.getFullYear()}`;
});

const diasDoMes = computed(() => {
  const ano = mesVisivel.value.getFullYear();
  const mes = mesVisivel.value.getMonth();
  const inicio = new Date(ano, mes, 1);
  const offset = inicio.getDay();
  const total =
    Math.ceil((offset + new Date(ano, mes + 1, 0).getDate()) / 7) * 7;
  const hoje = new Date();

  return Array.from({ length: total }, (_, i) => {
    const d = new Date(ano, mes, 1 - offset + i);
    return {
      data: d,
      dia: d.getDate(),
      doMes: d.getMonth() === mes,
      hoje: d.toDateString() === hoje.toDateString(),
      bloqueado: props.noPast && anteriorAHoje(d),
      escolhida:
        !!selecionada.value &&
        d.toDateString() === selecionada.value.toDateString(),
    };
  });
});

const horas = Array.from({ length: 24 }, (_, i) => i);
const minutos = computed(() => {
  const passo = Math.max(1, Math.min(60, props.minuteStep));
  return Array.from({ length: Math.ceil(60 / passo) }, (_, i) => i * passo);
});

// Onde o minuto cai na coluna. Com passo 1 é ele mesmo.
function indiceDoMinuto(m) {
  const i = minutos.value.indexOf(m);
  return i >= 0 ? i : 0;
}

// A coluna de minutos tem 60 itens: abrir sempre no topo obrigaria a rolar
// até o valor atual toda vez.
function rolarParaHorario() {
  const centralizar = (lista, indice) => {
    const alvo = lista?.children?.[indice];
    if (alvo) lista.scrollTop = alvo.offsetTop - lista.clientHeight / 2 + 14;
  };
  centralizar(listaHoras.value, horaSelecionada.value);
  // Com passo > 1 a coluna tem menos itens que 60, então o MINUTO deixa de
  // ser o índice dele. Sem esta busca, abrir o seletor às 16:30 com passo 30
  // rolava para o item 30 — que não existe — e a coluna abria no fim.
  centralizar(listaMinutos.value, indiceDoMinuto(minutoSelecionado.value));
}

// Posição do painel em coordenadas de viewport.
//
// O painel era `position: absolute` dentro do campo, então QUALQUER ancestral
// com `overflow` o cortava — achado 2026-08-04 no painel de agendar da tela de
// Publicações: o calendário aparecia cortado embaixo e espremido de lado, com
// os dias invadindo a coluna das horas. O mesmo defeito estava latente nas
// outras 26 telas que usam este componente, em qualquer uma que tenha barra de
// rolagem.
//
// `fixed` + coordenadas calculadas na abertura resolve para todas de uma vez,
// sem mexer no markup de quem consome.
const posicao = ref({ top: 0, left: 0 });

const PAINEL_LARGURA = 330;
const PAINEL_ALTURA = 330;
const MARGEM = 8;

function posicionarPainel() {
  const ancora = raiz.value;
  if (!ancora) return;

  const r = ancora.getBoundingClientRect();
  // Depois de montado, vale o tamanho de verdade: o painel só-data (sem as
  // colunas de hora) é mais baixo e mais estreito que a estimativa, e aberto
  // para cima ficava solto, ~60px acima do campo (Central › Novo horário,
  // 07/10/2026). Na primeira conta (antes de montar) vale a estimativa.
  const altura = painel.value?.offsetHeight || PAINEL_ALTURA;
  const largura = painel.value?.offsetWidth || PAINEL_LARGURA;
  // Abre pra cima quando não cabe embaixo — em formulário longo o campo
  // costuma estar na metade de baixo da tela.
  const cabeAbaixo = window.innerHeight - r.bottom > altura + MARGEM;
  const top = cabeAbaixo ? r.bottom + 4 : Math.max(MARGEM, r.top - altura - 4);
  // E pra esquerda quando encostaria na borda direita.
  const left = Math.max(
    MARGEM,
    Math.min(r.left, window.innerWidth - largura - MARGEM)
  );

  posicao.value = { top, left };
}

function abrir() {
  if (props.disabled) return;
  aberto.value = !aberto.value;
  if (!aberto.value) return;
  mesVisivel.value = selecionada.value
    ? new Date(selecionada.value)
    : new Date();
  colunaAtiva.value = 'hora';
  limparBuffer();
  posicionarPainel();
  nextTick(() => {
    posicionarPainel();
    rolarParaHorario();
    painel.value?.focus();
  });
}

function escolherDia(item) {
  if (item.bloqueado) {
    avisarPassado();
    return;
  }
  const d = selecionada.value ? new Date(selecionada.value) : new Date();
  d.setFullYear(
    item.data.getFullYear(),
    item.data.getMonth(),
    item.data.getDate()
  );
  if (!selecionada.value && !props.dateOnly) d.setHours(9, 0, 0, 0);
  aplicarData(d);
  if (props.dateOnly) aberto.value = false;
}

function escolherHora(h) {
  const d = selecionada.value ? new Date(selecionada.value) : new Date();
  d.setHours(h);
  if (!selecionada.value) d.setMinutes(0);
  aplicarData(d);
  colunaAtiva.value = 'hora';
}

function escolherMinuto(m) {
  const d = selecionada.value ? new Date(selecionada.value) : new Date();
  d.setMinutes(m);
  aplicarData(d);
  colunaAtiva.value = 'minuto';
}

// ── Teclado dentro do seletor ───────────────────────────────────────────────
// Com o painel aberto, digitar tem que funcionar igual a clicar: "22" leva
// para as 22h, "31" leva para o minuto 31 (Victor, 2026-08-04). Ao completar
// a hora, a vez passa sozinha para o minuto — a ordem em que se digita.
function rolarAte(coluna, indice) {
  nextTick(() => {
    const lista = coluna === 'hora' ? listaHoras.value : listaMinutos.value;
    const pos = coluna === 'hora' ? indice : indiceDoMinuto(indice);
    const alvo = lista?.children?.[pos];
    if (alvo) lista.scrollTop = alvo.offsetTop - lista.clientHeight / 2 + 14;
  });
}

function digitarNoSeletor(digito) {
  const max = colunaAtiva.value === 'hora' ? 23 : 59;
  const candidato = Number(`${buffer.value}${digito}`);

  // "5" na hora só pode ser 05 — não existe 5x válido. Já "2" pode virar 22,
  // então espera o segundo dígito.
  const fecha = buffer.value.length === 1 || candidato * 10 > max;
  const valor = candidato > max ? Number(digito) : candidato;

  if (colunaAtiva.value === 'hora') {
    escolherHora(valor);
    rolarAte('hora', valor);
  } else {
    escolherMinuto(valor);
    rolarAte('minuto', valor);
  }

  if (fecha) {
    limparBuffer();
    // Hora completa → o próximo dígito é minuto, como em qualquer relógio.
    if (colunaAtiva.value === 'hora') colunaAtiva.value = 'minuto';
  } else {
    buffer.value = String(candidato);
    bufferTimer = setTimeout(limparBuffer, 900);
  }
}

function onKeydownSeletor(evento) {
  if (!aberto.value) return;

  if (evento.key === 'Escape') {
    aberto.value = false;
    return;
  }
  if (evento.key === 'Enter') {
    evento.preventDefault();
    aberto.value = false;
    return;
  }
  if (evento.key === 'Tab') {
    evento.preventDefault();
    colunaAtiva.value = colunaAtiva.value === 'hora' ? 'minuto' : 'hora';
    limparBuffer();
    return;
  }
  if (/^[0-9]$/.test(evento.key) && !props.dateOnly) {
    evento.preventDefault();
    digitarNoSeletor(evento.key);
  }
}

function agora() {
  const d = new Date();
  d.setSeconds(0, 0);
  aplicarData(d);
  mesVisivel.value = new Date(d);
}

function limpar() {
  texto.value = '';
  emitir('');
  aberto.value = false;
}

function mudarMes(delta) {
  const d = new Date(mesVisivel.value);
  d.setMonth(d.getMonth() + delta);
  mesVisivel.value = d;
}

// Pedido do Dr. Henrique via Victor (2026-08-07): "tem como colocar o
// calendário para pular para o mês seguinte e voltar para o mês anterior? Só
// aparece do mês vigente, daí fica ruim para contar o prazo."
//
// Prazo processual atravessa o ano (recurso em dezembro conta para janeiro), e
// só com as setas de mês seriam 12 cliques. `setMonth` antes de `setFullYear`
// não é necessário aqui: mudar só o ano de 31/01 nunca cai em dia inexistente,
// exceto 29/02 — e aí o próprio Date normaliza para 01/03, que é o
// comportamento certo para um calendário de navegação (ninguém está
// escolhendo a data ainda, só olhando o mês).
function mudarAno(delta) {
  const d = new Date(mesVisivel.value);
  d.setFullYear(d.getFullYear() + delta);
  mesVisivel.value = d;
}

function onClickFora(evento) {
  if (!aberto.value) return;
  if (raiz.value?.contains(evento.target)) return;
  // O painel mora no `body` desde que virou `Teleport`: sem esta linha,
  // escolher um dia contaria como clique fora e fecharia na cara de quem
  // clicou.
  if (painel.value?.contains(evento.target)) return;
  aberto.value = false;
}

// Com o painel em `fixed`, rolar a página deixaria ele parado no ar longe do
// campo. Reposicionar a cada scroll é o comportamento certo; `capture` pega
// também a rolagem de containers internos, que é o caso do painel de agendar.
function onScrollGlobal() {
  if (aberto.value) posicionarPainel();
}

onMounted(() => {
  document.addEventListener('mousedown', onClickFora, true);
  window.addEventListener('scroll', onScrollGlobal, true);
  window.addEventListener('resize', onScrollGlobal);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickFora, true);
  window.removeEventListener('scroll', onScrollGlobal, true);
  window.removeEventListener('resize', onScrollGlobal);
});
</script>

<template>
  <div ref="raiz" class="relative">
    <!-- 🔴 `autocomplete="off"` + `data-*-ignore`: quando este campo divide
         formulário com um campo de senha, o Chrome o trata como o "usuário" do
         par login/senha e escreve o e-mail de quem está logado dentro dele.
         Aconteceu em produção em 2026-08-17, na ficha do cliente: a Data de
         nascimento aparecia com o e-mail do advogado. Data nunca é credencial —
         a proteção fica no componente para valer em TODA data do sistema, e não
         só na tela onde o defeito apareceu. -->
    <input
      ref="campo"
      :value="texto"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      data-1p-ignore
      data-lpignore="true"
      :placeholder="mascara"
      :aria-label="rotulo || undefined"
      :disabled="disabled"
      :class="inputClass"
      class="advup-dt-field reset-base"
      @input="onInput"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <button
      type="button"
      class="advup-dt-toggle"
      :class="{ 'advup-dt-toggle--on': aberto }"
      :disabled="disabled"
      :aria-label="mascara"
      @click="abrir"
    >
      <span class="i-ph-calendar-blank size-4" aria-hidden="true" />
    </button>

    <p v-if="avisoPassado" class="advup-dt-aviso">
      {{ t('APPOINTMENTS.DATETIME.NO_PAST') }}
    </p>

    <!-- 🔴 `Teleport` PARA O BODY, e não um `fixed` qualquer.
         Victor, 09/09/2026: "todos que tem data aí, quando tento selecionar
         a data não abre nada". Abria — só não aparecia.
         `position: fixed` promete escapar de qualquer ancestral, mas essa
         promessa cai quando um ancestral cria contexto de empilhamento ou
         bloco contentor. No painel de agendar publicação o campo fica dentro
         de um cartão animado com `transform` e de uma área com
         `overflow-y: auto`: o seletor nascia preso ali, atrás do formulário.
         O `AdvupSelect`, no MESMO painel, já tinha aprendido isto — o
         comentário dele diz "quem resolve o corte é o Teleport para o body".
         Aqui ficou faltando. Fora do body, nenhum ancestral alcança o painel.

         `tabindex` + foco automático: o painel precisa receber tecla para o
         "22" virar 22h. Sem isso o navegador manda o dígito para lugar
         nenhum. -->
    <Teleport v-if="aberto" to="body">
      <div
        ref="painel"
        class="advup-dt-pop"
        :style="{ top: `${posicao.top}px`, left: `${posicao.left}px` }"
        tabindex="-1"
        @keydown="onKeydownSeletor"
      >
        <div class="advup-dt-cal">
          <!-- Duplas = ano, simples = mês. Ordem « ‹ título › » — a mesma de
             qualquer calendário, para não precisar de rótulo. -->
          <div class="advup-dt-cal-head">
            <button
              v-tooltip.top="t('APPOINTMENTS.DATETIME.PREV_YEAR')"
              type="button"
              class="advup-dt-nav"
              :aria-label="t('APPOINTMENTS.DATETIME.PREV_YEAR')"
              @click="mudarAno(-1)"
            >
              <span class="i-ph-caret-double-left size-4" aria-hidden="true" />
            </button>
            <button
              v-tooltip.top="t('APPOINTMENTS.DATETIME.PREV_MONTH')"
              type="button"
              class="advup-dt-nav"
              :aria-label="t('APPOINTMENTS.DATETIME.PREV_MONTH')"
              @click="mudarMes(-1)"
            >
              <span class="i-ph-caret-left size-4" aria-hidden="true" />
            </button>
            <span class="advup-dt-mes">{{ tituloMes }}</span>
            <button
              v-tooltip.top="t('APPOINTMENTS.DATETIME.NEXT_MONTH')"
              type="button"
              class="advup-dt-nav"
              :aria-label="t('APPOINTMENTS.DATETIME.NEXT_MONTH')"
              @click="mudarMes(1)"
            >
              <span class="i-ph-caret-right size-4" aria-hidden="true" />
            </button>
            <button
              v-tooltip.top="t('APPOINTMENTS.DATETIME.NEXT_YEAR')"
              type="button"
              class="advup-dt-nav"
              :aria-label="t('APPOINTMENTS.DATETIME.NEXT_YEAR')"
              @click="mudarAno(1)"
            >
              <span class="i-ph-caret-double-right size-4" aria-hidden="true" />
            </button>
          </div>

          <div class="advup-dt-grid advup-dt-semana">
            <span v-for="(d, i) in DIAS_SEMANA" :key="i">{{ d }}</span>
          </div>

          <div class="advup-dt-grid">
            <button
              v-for="item in diasDoMes"
              :key="item.data.toISOString()"
              type="button"
              class="advup-dt-dia"
              :class="{
                'advup-dt-dia--fora': !item.doMes,
                'advup-dt-dia--hoje': item.hoje,
                'advup-dt-dia--on': item.escolhida,
                'advup-dt-dia--bloqueado': item.bloqueado,
              }"
              :disabled="item.bloqueado"
              @click="escolherDia(item)"
            >
              {{ item.dia }}
            </button>
          </div>
        </div>

        <template v-if="!dateOnly">
          <div
            ref="listaHoras"
            class="advup-dt-col"
            :class="{ 'advup-dt-col--ativa': colunaAtiva === 'hora' }"
            @click="colunaAtiva = 'hora'"
          >
            <button
              v-for="h in horas"
              :key="h"
              type="button"
              class="advup-dt-tempo"
              :class="{ 'advup-dt-tempo--on': h === horaSelecionada }"
              @click="escolherHora(h)"
            >
              {{ String(h).padStart(2, '0') }}
            </button>
          </div>
          <div
            ref="listaMinutos"
            class="advup-dt-col"
            :class="{ 'advup-dt-col--ativa': colunaAtiva === 'minuto' }"
            @click="colunaAtiva = 'minuto'"
          >
            <button
              v-for="m in minutos"
              :key="m"
              type="button"
              class="advup-dt-tempo"
              :class="{ 'advup-dt-tempo--on': m === minutoSelecionado }"
              @click="escolherMinuto(m)"
            >
              {{ String(m).padStart(2, '0') }}
            </button>
          </div>
        </template>

        <div class="advup-dt-rodape">
          <button type="button" class="advup-dt-acao" @click="limpar">
            {{ t('APPOINTMENTS.DATETIME.CLEAR') }}
          </button>
          <div class="advup-dt-rodape-dir">
            <button type="button" class="advup-dt-acao" @click="agora">
              {{
                dateOnly
                  ? t('APPOINTMENTS.DATETIME.TODAY')
                  : t('APPOINTMENTS.DATETIME.NOW')
              }}
            </button>
            <!-- Confirmar: o valor já vai sendo aplicado a cada clique, mas sem
               um botão de fechar a pessoa fica sem saber se "pegou" (Victor,
               2026-08-04). -->
            <button type="button" class="advup-dt-ok" @click="aberto = false">
              {{ t('APPOINTMENTS.DATETIME.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* O recuo à direita é a LARGURA DO BOTÃO de calendário que mora dentro do
   campo — medida de controle, não espaço de ritmo, por isso vem de variável
   própria e não da grade de 4.
   O `!important` saiu: ele existia para vencer o `padding` do reset global
   de `input[type]`, e o campo já carrega `reset-base`, que é justamente o
   que aquele seletor exclui. Contra-regra é sintoma. */
.advup-dt-field {
  --dt-botao-alvo: 34px;

  padding-right: var(--dt-botao-alvo);
  font-variant-numeric: tabular-nums;
}
/* Discreto: só o ícone, sem caixa cinza por cima do campo. */
.advup-dt-toggle {
  position: absolute;
  top: 50%;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--slate-10));
  background: none;
  transform: translateY(-50%);
}
/* 24px de altura é abaixo dos 44px que o padrão pede para o dedo. Só a área de toque
 * cresce, e só no celular.
 * ⚠️ NÃO declarar `position` aqui: o botão já é `absolute` e o `::after`
 * ancora nele. Declarar `relative` tiraria o botão do lugar. */
@media (max-width: 639px) {
  .advup-dt-toggle::after {
    content: '';
    position: absolute;
    inset: -10px 0;
  }
}

.advup-dt-toggle:hover,
.advup-dt-toggle--on {
  color: rgb(var(--blue-9));
}

.advup-dt-aviso {
  margin-top: var(--esp-1);
  font-size: var(--txt-selo);
  color: rgb(var(--ruby-9));
}
.advup-dt-pop {
  /* `fixed` e não `absolute`: escapa de qualquer ancestral com `overflow`.
     Ver `posicionarPainel()` — as coordenadas vêm de lá. */
  position: fixed;
  /* O mesmo 9999 do `AdvupSelect`. Com 60 o seletor perdia para qualquer
     modal do produto — e perder aqui é ficar invisível, não ficar atrás. */
  z-index: 9999;
  display: grid;
  grid-template-columns: auto auto auto;
  padding: var(--esp-2);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
  gap: var(--esp-1);
}
.advup-dt-cal {
  min-width: 196px;
}
.advup-dt-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--esp-1);
}
.advup-dt-mes {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  /* O título é o único item elástico da linha; sem isto ele quebraria em
     duas e desalinharia as setas em meses de nome longo (setembro, novembro,
     dezembro). */
  white-space: nowrap;
}
.advup-dt-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--r-2);
  color: rgb(var(--slate-11));
  flex-shrink: 0;
}

/*
  POR QUE ESTA REGRA EXISTE — as setas já estavam aqui e ninguém as via.

  O ícone é um `<span>` com máscara CSS, sem conteúdo. Dentro de um pai
  `inline-flex`, um item sem conteúdo tem largura base 0 e o `width` de
  `size-4` não impede o flex de encolhê-lo: medido em produção,
  `getBoundingClientRect().width` = **2px** (a altura, essa vinha certa: 16px).
  O resultado era um risco vertical cinza de 2px em vez de uma seta.

  Foi o Dr. Henrique quem reportou, e como "não dá para mudar de mês" — a
  navegação funcionava desde sempre, só era invisível. Vale a lição: botão que
  ninguém enxerga é botão que não existe.
*/
.advup-dt-nav > span {
  flex-shrink: 0;
}
.advup-dt-nav:hover {
  background: rgb(0 0 0 / 5%);
}
.advup-dt-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
}
.advup-dt-semana {
  margin-bottom: 0;
  font-size: var(--txt-selo);
  color: rgb(var(--slate-10));
  text-align: center;
}
.advup-dt-dia {
  height: 24px;
  border-radius: var(--r-2);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-12));
}
.advup-dt-dia:hover {
  background: rgb(31 111 235 / 10%);
}
/* Dia anterior a hoje: some do alcance sem sumir da grade — a pessoa precisa
   ver o mês inteiro para se localizar. */
.advup-dt-dia--bloqueado {
  color: rgb(var(--slate-7));
  cursor: not-allowed;
  text-decoration: line-through;
}
.advup-dt-dia--bloqueado:hover {
  background: none;
}
.advup-dt-dia--fora {
  color: rgb(var(--slate-7));
}
.advup-dt-dia--hoje {
  box-shadow: inset 0 0 0 1px rgb(var(--blue-9));
}
.advup-dt-dia--on {
  background: rgb(var(--blue-9));
  color: #fff;
}
.advup-dt-dia--on:hover {
  background: rgb(var(--blue-10));
}

/* Colunas de hora e minuto: largas o bastante para o número respirar, com a
   barra de rolagem escondida — 24 e 60 itens roláveis não precisam anunciar
   que rolam. */
.advup-dt-col {
  width: 54px;
  max-height: 196px;
  overflow-y: auto;
  border-left: 1px solid rgb(var(--slate-4));
  padding-left: var(--esp-1);
  scrollbar-width: none;
  scroll-snap-type: y proximity;
}
.advup-dt-col::-webkit-scrollbar {
  display: none;
}
/* A coluna que vai receber o que for digitado. Sem essa marca, "22" mudaria
   a hora ou o minuto sem a pessoa saber qual. */
.advup-dt-col--ativa {
  background: rgb(31 111 235 / 6%);
  border-radius: var(--r-3);
}
.advup-dt-tempo {
  display: block;
  width: 100%;
  padding: var(--esp-1) 0;
  border-radius: var(--r-2);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-12));
  font-variant-numeric: tabular-nums;
  scroll-snap-align: center;
}
.advup-dt-tempo:hover {
  background: rgb(31 111 235 / 10%);
}
.advup-dt-tempo--on {
  background: rgb(var(--blue-9));
  color: #fff;
}

.advup-dt-rodape {
  display: flex;
  justify-content: space-between;
  grid-column: 1 / -1;
  margin-top: var(--esp-1);
  padding-top: var(--esp-2);
  border-top: 1px solid rgb(var(--slate-4));
}
.advup-dt-rodape-dir {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}
.advup-dt-ok {
  padding: var(--esp-1) var(--esp-3);
  border-radius: var(--r-2);
  background: rgb(var(--blue-9));
  color: #fff;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
}
.advup-dt-ok:hover {
  background: rgb(var(--blue-10));
}
.advup-dt-acao {
  font-size: var(--txt-nota);
  color: rgb(var(--blue-9));
}
.advup-dt-acao:hover {
  text-decoration: underline;
}
</style>
